import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import productsApi from '../../../api/productsApi';
import { applyServerErrors, getErrorMessage } from '../../../utils/errorHandler';

const schema = yup.object({
  name: yup.string().trim().required('El nombre es obligatorio').max(255),
  slug: yup.string().nullable(),
  logo: yup.string().nullable().max(2048, 'La URL es demasiado larga'),
});

const emptyValues = {
  name: '',
  slug: '',
  logo: '',
};

// Formulario de alta/edicion de una marca. `brand` nulo significa "crear nueva".
// El slug es opcional: si se deja vacio, el backend lo genera a partir del nombre.
export default function BrandFormDialog({ open, onClose, onSaved, brand }) {
  const { enqueueSnackbar } = useSnackbar();
  const isEditing = Boolean(brand);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      brand
        ? {
            name: brand.name,
            slug: brand.slug || '',
            logo: brand.logo || '',
          }
        : emptyValues
    );
  }, [open, brand, reset]);

  const onSubmit = async (data) => {
    const payload = {
      name: data.name,
      slug: data.slug || undefined,
      logo: data.logo || null,
    };

    try {
      if (isEditing) {
        await productsApi.updateBrand(brand.id, payload);
        enqueueSnackbar('Marca actualizada correctamente.', { variant: 'success' });
      } else {
        await productsApi.createBrand(payload);
        enqueueSnackbar('Marca creada correctamente.', { variant: 'success' });
      }
      onSaved();
    } catch (error) {
      const handled = applyServerErrors(error, setError);
      if (!handled) {
        enqueueSnackbar(getErrorMessage(error, 'No se pudo guardar la marca.'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar marca' : 'Nueva marca'}</DialogTitle>

      <DialogContent dividers>
        <Stack component="form" id="brand-form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
          {errors.root?.message && <Alert severity="error">{errors.root.message}</Alert>}

          <TextField
            label="Nombre"
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name?.message}
            {...register('name')}
          />

          <TextField
            label="Slug (opcional)"
            fullWidth
            placeholder="Se genera automáticamente si lo dejas vacío"
            error={Boolean(errors.slug)}
            helperText={errors.slug?.message}
            {...register('slug')}
          />

          <TextField
            label="URL del logo (opcional)"
            fullWidth
            error={Boolean(errors.logo)}
            helperText={errors.logo?.message}
            {...register('logo')}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" form="brand-form" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BrandFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
  brand: PropTypes.object,
};

BrandFormDialog.defaultProps = {
  brand: null,
};
